import React, { useMemo } from 'react';
import { AppButton, Box, ExpressSetupCopaysComponent, ExpressSetupPlansComponent, Inscription, ShimmerBox } from '@common-fe/common-fe';

import { LetterIconSizeEnum, OrganizationTypes } from '@/common/constants';
import { UserLetterIcon } from '@/components/elements/LetterIcon/UserLetterIcon';

import { ExpressSetup } from '../ExpressSetup.types';

import { ExpressSetupSummaryItem } from './ExpressSetupSummaryItem';

const MAX_COPAYS = 6;

interface Props {
  expressSetupData?: ExpressSetup;
  isLoading?: boolean;
  handleOnClickMainContact?: () => void;
  handleOnClickBankAccount?: () => void;
  handleOnClickPlans?: () => void;
}

export const ExpressSetupSummaryMainTiles: React.FC<Props> = ({
  expressSetupData,
  isLoading,
  handleOnClickMainContact,
  handleOnClickBankAccount,
  handleOnClickPlans,
}) => {
  const mainContact = useMemo(
    () => expressSetupData?.contacts?.find((contact) => contact?.isPrimary),
    [ expressSetupData?.contacts ]);
  const last4 = useMemo(
    () => expressSetupData?.accountNumberLast4,
    [ expressSetupData?.accountNumberLast4 ]);
  const plans = useMemo(
    () => expressSetupData?.plans,
    [ expressSetupData?.plans ]);
  const copays = useMemo(
    () => expressSetupData?.copays,
    [ expressSetupData?.copays ]);
  return (
    <Box gap="spacing12" data-testid="ExpressSetupSummaryMainTiles">
      {
        isLoading ? <ShimmerBox width="100%" height="72px" /> : (
          <ExpressSetupSummaryItem
            title="Main Contact"
            isCompleted={!!mainContact}
            onClick={handleOnClickMainContact}
            actionNode={mainContact ? (
              <Box title={mainContact?.contactName}>
                <UserLetterIcon
                  testId='contact-info-icon'
                  type={OrganizationTypes.partner}
                  size={LetterIconSizeEnum.LOGO}
                  text={mainContact?.contactName}
                />
              </Box>
            )
              : (
                <AppButton
                  testId='add-main-contact'
                  onClick={handleOnClickMainContact}
                  width="175px"
                >
                  Add Main Contact
                </AppButton>
              )}
          />
        )
      }

      {
        isLoading ? <ShimmerBox width="100%" height="72px" /> : (
          <ExpressSetupSummaryItem
            title="Bank Account"
            isCompleted={!!last4}
            onClick={handleOnClickBankAccount}
            actionNode={last4 ? (
              <Box direction="row" align="center" gap="spacing4">
                <Inscription size="16px" color="textBody" lineHeight="22px">
                  Ending in
                </Inscription>
                <Inscription size="16px" color="textBody" lineHeight="22px" weight={700}>
                  {last4}
                </Inscription>
              </Box>
            )
              : (
                <AppButton
                  testId='add-bank-account'
                  onClick={handleOnClickBankAccount}
                  width="175px"
                >
                  Link Bank Account
                </AppButton>
              )}
          />
        )
      }

      {
        isLoading ? <ShimmerBox width="100%" height="72px" /> : (
          <ExpressSetupSummaryItem
            title="Plan Year & Plans"
            isCompleted={!!(plans && plans.length)}
            onClick={handleOnClickPlans}
            actionNode={(!plans || !plans.length) ? (
              <AppButton
                testId='add-plan'
                onClick={handleOnClickPlans}
                width="175px"
              >
                Configure
              </AppButton>
            ) : null}
            infoNode={(plans && plans.length) ? (
              <Box>
                <ExpressSetupPlansComponent
                  plans={plans}
                  planStartDate={expressSetupData?.planStartDate}
                  planEndDate={expressSetupData?.planEndDate}
                  modalMode
                  showMoreText='Plans'
                />
                {copays && copays.length ? (
                  <ExpressSetupCopaysComponent
                    copays={copays}
                    rowFormation
                    numberOfCopaysToDisplay={MAX_COPAYS}
                    showMoreText='Copays'
                  />
                ) : null}
              </Box>
            ) : null}
          />
        )
      }
    </Box>
  );
};
